import styled from '@emotion/styled';
import { Button, message, Result, Spin } from 'antd';
import Empty from 'components/Empty';
import { get } from 'lodash-es';
import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { getTalkInfoByBuyerStore } from 'services/api/refund';
import {
  detail as detailApi,
  stat,
  updateStatus as updateStatusApi,
  getStatBody,
} from 'services/api/review';
import { useSetReviewStat } from 'components/HorizontalNav/useReviewStatState';

import { createFakeConversation, ReviewContextProvider } from './Context';
import Detail from './Detail';
import useDetail from './hooks/useDetail';
import { currentReviewProfileState, reviewListState } from './hooks/useReview';
import NewList from './NewList';
import { ReviewSider } from './Sider';
import { reviewChannelMap } from 'utils/constants';
import { useGetConversationById } from 'hooks/business/lruConversation';
import { getPlatformExternalBuyerId } from 'utils/search';

const Container = styled.div`
  display: flex;
  flex: 1 0 0;
  overflow: hidden;
`;

const NewLeft = styled(NewList)`
  flex: 0 0 320px;
`;

const Center = styled(Detail)`
  flex: 1 0 0;
  border-left: solid 1px #e1e4ea;
  border-right: solid 1px #e1e4ea;
`;

const Right = styled(ReviewSider)`
  flex: 0 0 420px;
`;

const EmptyWrapper = styled.div`
  flex: 1 0 0;
  display: flex;
  justify-content: space-around;
  align-items: center;
  border-left: solid 1px #e1e4ea;
`;

function Review(props: HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslate();

  const currentReview = useAtomValue(currentReviewProfileState);

  const setCurrentReview = useSetAtom(currentReviewProfileState);

  const setReviewList = useSetAtom(reviewListState);

  const setReviewStat = useSetReviewStat();

  const getConversationById = useGetConversationById();

  const [stateConversation, setStateConversation] = useState<Conversation.Conversation>(() =>
    createFakeConversation(),
  );

  const [loading, setLoading] = useState(false);

  const { item: detail, fetch: fetchDetail, setItem: setDetail } = useDetail();

  // 可能存在 conversation 一开始不在 map 里，后来被添加到 map 里的情况。应该优先从 map 里取。
  const conversation =
    detail?.conversationId && detail?.conversationId !== '0'
      ? getConversationById(detail?.conversationId) || stateConversation
      : stateConversation;

  const refsCurrent = {
    fetchDetail,
    setDetail,
    currentReview,
    t,
    setCurrentReview,
    setReviewList,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const refreshReview = useCallback(async () => {
    if (!currentReview) {
      return;
    }

    const { commentId, status } = currentReview;

    try {
      const detail = await detailApi({
        commentId,
      });
      setDetail(detail);

      if (detail.status === status) {
        return;
      }

      setCurrentReview((pre) => {
        if (!pre || pre.status === detail.status || pre.commentId !== commentId) {
          return pre;
        }

        return {
          ...pre,
          status: detail.status,
        };
      });

      setReviewList((cur) => {
        return {
          ...cur,
          list: cur.list.map((review) => {
            if (review.commentId !== commentId) {
              return review;
            }

            return {
              ...review,
              status: detail.status,
            };
          }),
        };
      });
    } catch (err) {}
  }, [currentReview, setDetail, setCurrentReview, setReviewList]);

  const updateReviewStat = useCallback(async () => {
    try {
      const res = await stat(getStatBody());
      setReviewStat(res);
    } catch (err) {
      message.error(t('review.获取评价管理统计失败'));
    }
  }, [setReviewStat, t]);

  const updateStatus = useCallback(
    async (status: Review.Status, quiet: boolean = false) => {
      if (!currentReview) {
        return;
      }
      const { commentId } = currentReview;
      try {
        await updateStatusApi({
          commentId,
          status,
        });
        updateReviewStat();
        await refreshReview();
        if (quiet) {
          return;
        }

        message.success(t('review.评价更新成功'));
      } catch (err) {
        if (quiet) {
          return;
        }

        if (get(err, 'response.status') === 409) {
          message.warn(t('review评价已被处理，请去处理其他评价吧'));
        } else {
          message.error(t('review更新评价状态失败，请稍后再试'));
        }
      }
    },
    [currentReview, refreshReview, t, updateReviewStat],
  );

  useEffect(() => {
    if (!currentReview?.commentId) {
      return;
    }

    const {
      current: { fetchDetail, setDetail },
    } = refs;

    setDetail(undefined);

    setLoading(true);

    // eslint-disable-next-line prefer-destructuring
    const commentId = currentReview.commentId;

    fetchDetail({
      commentId,
    })
      .then((detail) => {
        const {
          current: { currentReview, t },
        } = refs;

        if (!detail || !currentReview) {
          return;
        }

        // 防止评价切换时，请求返回顺序不一致（如后选中的评价，详情接口却先返回）的问题。其他比较判断同理。
        // https://www.teambition.com/task/623c5818be6f71003f51409c
        if (currentReview.commentId !== commentId) {
          return;
        }

        const {
          conversationId,
          internalStoreId,
          channel,
          region,
          storeId: externalStoreId,
          buyerId: externalBuyerId,
          orderId,
        } = detail;
        const platform = reviewChannelMap[channel];

        if (conversationId && conversationId !== '0') {
          const cachedConversation = getConversationById(conversationId);

          if (cachedConversation) {
            setStateConversation(cachedConversation);

            setLoading(false);

            return;
          }
        }

        const { userName, userPicture } = currentReview;

        setStateConversation(
          createFakeConversation({
            storeId: internalStoreId,
            buyerNick: userName,
            buyerAvatar: userPicture,
            region,
            channel: platform,
          }),
        );

        getTalkInfoByBuyerStore({
          items: [
            {
              channel: platform,
              region,
              externalStoreId,
              externalBuyerId: getPlatformExternalBuyerId(platform, externalBuyerId, orderId),
            },
          ],
          needPhone: true,
        })
          .then((res) => {
            const {
              current: { currentReview },
            } = refs;

            if (currentReview?.commentId !== commentId) {
              return;
            }

            const {
              talkInfos: [conversation],
            } = res;

            if (conversation && conversation.conversationId !== '0') {
              setStateConversation(conversation);
            }

            setLoading(false);
          })
          .catch(() => {
            const {
              current: { currentReview },
            } = refs;

            if (currentReview?.commentId !== commentId) {
              return;
            }

            message.error(t('review.获取会话信息失败，请稍后再试'));

            setLoading(false);
          });
      })
      .catch(() => {
        const {
          current: { currentReview },
        } = refs;

        if (currentReview?.commentId !== commentId) {
          return;
        }

        setLoading(false);
      });
  }, [currentReview?.commentId, getConversationById]);

  // detail 状态和列表不一致时，更新列表的状态。
  useEffect(() => {
    const {
      current: { currentReview, setReviewList, setCurrentReview },
    } = refs;

    if (!detail || !currentReview) {
      return;
    }

    if (detail.status !== currentReview.status) {
      setCurrentReview({
        ...currentReview,
        status: detail.status,
      });

      setReviewList((pre) => {
        return {
          ...pre,
          list: pre.list.map((review) => {
            if (review.commentId !== currentReview.commentId) {
              return review;
            }

            return {
              ...review,
              status: detail.status,
            };
          }),
        };
      });
    }
  }, [detail]);

  if (!currentReview) {
    return (
      <ReviewContextProvider value={null}>
        <Container {...props}>
          <NewLeft />
          <EmptyWrapper>
            <Empty description="" />
          </EmptyWrapper>
        </Container>
      </ReviewContextProvider>
    );
  }

  if (loading) {
    return (
      <ReviewContextProvider value={null}>
        <Container {...props}>
          <NewLeft />
          <EmptyWrapper>
            <Spin spinning />
          </EmptyWrapper>
        </Container>
      </ReviewContextProvider>
    );
  }

  if (!detail) {
    return (
      <ReviewContextProvider value={null}>
        <Container {...props}>
          <NewLeft />
          <EmptyWrapper>
            <Result
              status="500"
              title={t('review.评价详情加载失败')}
              subTitle={t('review.请稍后尝试手动重新加载')}
              extra={
                <Button
                  type="primary"
                  onClick={() => {
                    return fetchDetail({ commentId: currentReview.commentId }).catch(() => {});
                  }}
                >
                  {t('review.立即重新加载')}
                </Button>
              }
            />
          </EmptyWrapper>
        </Container>
      </ReviewContextProvider>
    );
  }

  return (
    <ReviewContextProvider
      value={{
        conversation,
        profile: currentReview,
        setProfile: setCurrentReview,
        detail,
        setDetail,
        refreshReview,
        updateStatus,
        onConversationCreated: setStateConversation,
      }}
    >
      <Container {...props}>
        <NewLeft />
        <Center />
        <Right />
      </Container>
    </ReviewContextProvider>
  );
}

export default Review;
